import { Component, EventEmitter, Input, OnChanges, OnInit, Output, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RequestCallBannerComponent } from '../request-call-banner/request-call-banner.component';
import { CallScheduledBannerComponent } from '../call-scheduled-banner/call-scheduled-banner.component';
import { CallCompletedBannerComponent } from '../call-completed-banner/call-completed-banner.component';
import { DoctorCallUnsuccessfulBannerComponent } from '../doctor-call-unsuccessful-banner/doctor-call-unsuccessful-banner.component';
import { AppConfig } from '../../../app.config';
import { UnpaidPendingCallService } from '@services/pending-call-banner-service';
import { UnpaidUserDoctorCallVariant, PendingCall, PendingCallBannerState } from '@services/pending-call-banner-service/types';
import { Table } from '@cureskin/api-client';

@Component({
  selector: 'app-unpaid-user-doctor-call-banner-container',
  standalone: true,
  imports: [
    CommonModule,
    RequestCallBannerComponent,
    CallScheduledBannerComponent,
    CallCompletedBannerComponent,
    DoctorCallUnsuccessfulBannerComponent
  ],
  templateUrl: './unpaid-user-doctor-call-banner-container.component.html'
})
export class UnpaidUserDoctorCallBannerContainerComponent implements OnInit, OnChanges {
  @Input() user?: Table.User;
  @Input() regimen?: { objectId: string; class: string };
  @Input() experimentVariant?: UnpaidUserDoctorCallVariant;
  @Input() pendingCalls?: PendingCall[];
  @Input() viewMode: 'regimen' | 'support' = 'regimen';
  @Input() isRegimenPreparationTimePending?: boolean = false;

  @Input() bannerState?: PendingCallBannerState;

  @Output() callCreated = new EventEmitter<void>();
  @Output() stateChange = new EventEmitter<PendingCallBannerState>();

  private internalBannerState: PendingCallBannerState = {
    isEligible: false,
    status: null,
    hideBanner: false,
    variantData: null,
    checkComplete: false
  };

  constructor(
    public readonly appConfig: AppConfig,
    private readonly unpaidPendingCallService: UnpaidPendingCallService,
    private readonly changeDetector: ChangeDetectorRef
  ) {}

  async ngOnInit(): Promise<void> {
    await this.handleInit()
  }

  async ngOnChanges(): Promise<void> {
    await this.handleInit();
  }

  async handleInit(): Promise<void> {
    if (this.shouldInitialize()) {
      await this.initializeBannerState();
    }
  }

  private shouldInitialize(): boolean {
    return !this.bannerState && !!this.user && !!this.regimen && !!this.experimentVariant;
  }

  private async initializeBannerState(): Promise<void> {
    this.internalBannerState = await this.unpaidPendingCallService.calculateBannerState(
      this.experimentVariant,
      this.user,
      this.regimen.class,
      this.pendingCalls,
      this.isRegimenPreparationTimePending
    );

    this.stateChange.emit(this.internalBannerState);
    this.changeDetector.markForCheck();
  }

  get currentBannerState(): PendingCallBannerState {
    return this.bannerState || this.internalBannerState;
  }

  get shouldShowRequestBanner(): boolean {
    const state = this.currentBannerState;
    return state.isEligible && !state.hideBanner && !state.status;
  }

  get shouldShowStatusBanner(): boolean {
    const state = this.currentBannerState;
    return state.isEligible && !state.hideBanner && !!state.status;
  }

  get bannerStatus(): string | null {
    return this.currentBannerState.status || null;
  }

  get containerClasses(): string {
    if (this.currentBannerState.variantData?.newBannerUI) {
      return '';
    } else {
      return this.viewMode === this.appConfig.Shared.PAGES.SUPPORT
        ? 'tw-px-4 tw-pb-3 tw-bg-white -tw-mt-1'
        : 'tw-px-4 tw-pb-4 tw-bg-white';
    }
  }

  get isRequestedStatus(): boolean {
    return this.bannerStatus === this.appConfig.Shared.PendingCall.Status.Requested;
  }

  get isCallCompletedStatus(): boolean {
    return this.bannerStatus === this.appConfig.Shared.PendingCall.Status.CallCompleted;
  }

  get isUnsuccessfulStatus(): boolean {
    return this.bannerStatus === this.appConfig.Shared.PendingCall.Status.CallLater ||
           this.bannerStatus === this.appConfig.Shared.PendingCall.Status.NotReachable;
  }

  async onCallCreated(): Promise<void> {
    this.callCreated.emit();

    if (this.shouldInitialize()) {
      await this.initializeBannerState();
    }
  }
}

